import React, { FC } from "react";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../hooks/Auth/useAuth";
import { AuthErrorMessages } from "../../@core/constants/AuthErrorMessages";
import { useLoader } from "../../@shared/hooks/useLoader";

export interface PageSignUpProps {
  className?: string;
}

interface IRegisterForm {
  email: '',
  password: '',
  name: ''
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const {
    createUserWithEmailAndPassword,
    handleAuthentication,
    errorCreateUser: error,
    socials
  } = useAuth();
  const { handleSubmit, formState: { errors }, register } = useForm<IRegisterForm>({
    defaultValues: {
      email: '',
      password: ''
    }
  });
  const loader = useLoader();

  const handleSubmitForm = async (data: IRegisterForm) => {
    try {
      loader.start();

      const newAuthentication = await createUserWithEmailAndPassword(data.email, data.password);

      if (!newAuthentication) return;

      await handleAuthentication(newAuthentication, { name: data.name });
    } catch (error) {
      console.error("Error signing in:", error);
    } finally {
      loader.stop();
    }
  };

  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <Helmet>
        <title>Sign up</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Signup
        </h2>
        <div className="max-w-md mx-auto space-y-6 ">
          <div className="grid gap-3">
            {socials.map((item, index) => (
              <div
                onClick={item.onClick}
                key={index}
                className="nc-will-change-transform flex w-full hover:cursor-pointer duration-300 rounded-lg bg-primary-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
              >
                <img
                  className="flex-shrink-0"
                  src={item.icon}
                  alt={item.name}
                />
                <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                  {item.name}
                </h3>
              </div>
            ))}
          </div>
          {/* OR */}
          <div className="relative text-center">
            <span className="relative z-10 inline-block px-4 font-medium text-sm bg-white dark:text-neutral-400 dark:bg-neutral-900">
              OR
            </span>
            <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-100 dark:border-neutral-800"></div>
          </div>
          {/* FORM */}
          <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit(handleSubmitForm)}>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Name
              </span>
              <Input
                type="text"
                className="mt-1"
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 5,
                    message: "Must be at least 5 characters"
                  }
                })}
              />
              {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
            </label>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Email address
              </span>
              <Input
                type="email"
                placeholder="example@example.com"
                className="mt-1"
                {...register("email", {
                  required: "Email is required"
                })}
              />
              {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}
            </label>
            <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                Password
              </span>
              <Input
                type="password" className="mt-1" {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters"
                }
              })}/>
              {errors.password && <span className="text-red-500 text-sm">{errors.password.message}</span>}
            </label>

            {error && (
              <span className="text-red-500 text-sm">
                {`${AuthErrorMessages[error?.message]}` || error.message}
              </span>
            )}
            <ButtonPrimary type="submit">Continue</ButtonPrimary>
          </form>

          {/* ==== */}
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            Already have an account? {` `}
            <Link to="/login">Sign in</Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;
